/**
 * Fraîcheur de la claim `mfaAt` — fonction PURE, sans dépendance Firebase.
 *
 * ## Pourquoi ce module existe (#1267)
 *
 * La fenêtre de 12 h a déjà trois miroirs : `firestore.rules` (`hasRecentMfa`),
 * `functions/src/utils/mfa-claims.ts` (`isMfaFresh`) et `TwoFactorService`. En
 * ajouter un quatrième dans `PortalAuthGuard` aurait été le prochain à diverger.
 *
 * Mais injecter `TwoFactorService` dans le garde ne marchait pas non plus : ce
 * service dépend de `Functions` (Firebase Callable), et le garde est construit
 * par DI dans une douzaine de specs qui ne fournissent pas ce provider — elles
 * échouaient toutes sur `NG0201: No provider found for Functions`. Le garde
 * n'appelle aucune Cloud Function ; il n'a aucune raison d'en traîner le graphe.
 *
 * D'où ce module : le CALCUL vit ici, sans dépendance ; `TwoFactorService` et
 * `PortalAuthGuard` l'appellent tous les deux. Un seul endroit porte la
 * fenêtre, et personne n'a besoin de Firebase pour la tester.
 */

/**
 * Fenêtre de fraîcheur, alignée sur `MFA_MAX_AGE_SECONDS` côté functions et sur
 * `mfaMaxAgeMillis()` dans `firestore.rules`. Ces trois valeurs doivent bouger
 * ensemble ou pas du tout.
 */
export const MFA_MAX_AGE_SECONDS = 12 * 60 * 60;

/**
 * Tolérance vers le futur : une horloge client légèrement en avance ne doit pas
 * faire passer une claim fraîche pour une anomalie.
 */
const MFA_FUTURE_TOLERANCE_SECONDS = 60;

/** Clé de session portant l'uid en attente de vérification 2FA. */
export const MFA_PENDING_UID_KEY = '2fa_pending_uid';

/**
 * La vérification portée par le token est-elle encore fraîche ?
 *
 * Trois réponses, et la troisième est celle qui compte le plus :
 *
 * - `true`  — claim fraîche : le serveur l'accepte, l'UI aussi ;
 * - `false` — claim PÉRIMÉE : `firestore.rules` refusera chaque lecture staff.
 *   C'est le cas qui, sans contrôle, casse en silence. Aussi `false` : une
 *   claim posée par une AUTRE connexion (`mfaAuthTime` absent ou différent de
 *   l'`auth_time` du jeton, #1943 lot 4) — le code est redemandé ;
 * - `null`  — la claim est ABSENTE : on **ne tranche pas**. Indispensable tant
 *   que la claim n'a pas circulé (jetons émis avant #866) — sans ce troisième
 *   état, le jour d'un déploiement, tout le staff serait renvoyé vers
 *   `/verify-2fa`, soit l'inverse exact du problème qu'on corrige.
 *
 * @param claims les custom claims du token décodé (`IdTokenResult.claims`)
 * @param nowSeconds l'instant de référence, en secondes — injectable pour les tests
 */
export function isMfaClaimFresh(
  claims: Record<string, unknown> | null | undefined,
  nowSeconds: number = Math.floor(Date.now() / 1000)
): boolean | null {
  const raw = claims?.['mfaAt'];
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  const age = nowSeconds - raw;
  if (age > MFA_MAX_AGE_SECONDS || age < -MFA_FUTURE_TOLERANCE_SECONDS) return false;
  // #1943 lot 4 — `mfaAt` est posée sur le COMPTE et recopiée dans tout nouveau
  // jeton : seule la connexion qui a validé le code (même `auth_time`) est
  // vérifiée. Une nouvelle connexion par mot de passe, ou une validation
  // antérieure au lot 4 (sans `mfaAuthTime`), redemande le code.
  const boundTo = claims?.['mfaAuthTime'];
  const authTime = claims?.['auth_time'];
  return typeof boundTo === 'number' && typeof authTime === 'number' && boundTo === authTime;
}
